import React, { ReactNode, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Drawer } from 'vaul';
import { useMountEffect } from '../../hooks/useMountEffect';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useWindowEvent } from '../../hooks/useWindowEvent';
import { acquireZIndex, releaseZIndex } from '../../utils/zIndexStack';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    maxWidth?: string;
    /**
     * When true, modal renders fullscreen on mobile (no bottom-sheet drag).
     * When false/undefined, mobile auto-uses a vaul bottom-sheet.
     * Auto-enabled for `maxWidth >= 600px` for backward compatibility with
     * the original Modal heuristic.
     */
    mobileFullScreen?: boolean;
    /** Optional styling scope for marketing overlays, including their portal. */
    className?: string;
}

const parsePxWidth = (value: string): number | null => {
    const match = /^(\d+(?:\.\d+)?)px$/.exec(value.trim());
    return match ? parseFloat(match[1]) : null;
};

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    maxWidth = '500px',
    mobileFullScreen,
    className,
}) => {
    return (
        <AnimatePresence>
            {isOpen ? (
                <ModalShell
                    onClose={onClose}
                    title={title}
                    footer={footer}
                    maxWidth={maxWidth}
                    mobileFullScreen={mobileFullScreen}
                    className={className}
                >
                    {children}
                </ModalShell>
            ) : null}
        </AnimatePresence>
    );
};

type ShellProps = Omit<ModalProps, 'isOpen'>;

const ModalShell: React.FC<ShellProps> = ({
    onClose,
    title,
    children,
    footer,
    maxWidth = '500px',
    mobileFullScreen,
    className,
}) => {
    const isMobile = useMediaQuery('(max-width: 639px)');
    const parsedWidth = parsePxWidth(maxWidth);
    const useFullScreenOnMobile =
        mobileFullScreen ?? (parsedWidth !== null && parsedWidth >= 600);
    // Mobile bottom-sheet only when not in fullscreen mode
    const useSheet = isMobile && !useFullScreenOnMobile;

    if (useSheet) {
        return (
            <SheetVariant
                onClose={onClose}
                title={title}
                footer={footer}
                className={className}
            >
                {children}
            </SheetVariant>
        );
    }

    return (
        <CenteredVariant
            onClose={onClose}
            title={title}
            footer={footer}
            maxWidth={maxWidth}
            useFullScreenOnMobile={useFullScreenOnMobile}
            className={className}
        >
            {children}
        </CenteredVariant>
    );
};

interface CenteredVariantProps extends Omit<ShellProps, 'mobileFullScreen'> {
    useFullScreenOnMobile?: boolean;
}

const CenteredVariant: React.FC<CenteredVariantProps> = ({
    onClose,
    title,
    children,
    footer,
    maxWidth = '500px',
    useFullScreenOnMobile,
    className,
}) => {
    const titleId = useId();
    const reduceMotion = useReducedMotion();
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveRef = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const [zIndex] = useState<number>(() => acquireZIndex());

    useBodyScrollLock();

    useMountEffect(() => {
        previousActiveRef.current =
            document.activeElement as HTMLElement | null;
        return () => {
            releaseZIndex();
            // Restore focus to the trigger element on close
            previousActiveRef.current?.focus?.();
        };
    });

    // Escape closes — capture phase so popovers above can intercept first
    useWindowEvent(
        'keydown',
        (e) => {
            if ((e as KeyboardEvent).key === 'Escape') {
                const dialogs = document.querySelectorAll(
                    '[role="dialog"][aria-modal="true"]',
                );
                if (dialogs[dialogs.length - 1]?.contains(modalRef.current)) {
                    onCloseRef.current();
                }
            }
        },
        false,
    );

    // Focus first focusable + Tab trap
    useMountEffect(() => {
        if (!modalRef.current) {
            return undefined;
        }
        const getFocusable = () =>
            Array.from(
                modalRef.current?.querySelectorAll<HTMLElement>(
                    'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
                ) ?? [],
            ).filter((element) => element.getClientRects().length > 0);

        const handleTab = (e: KeyboardEvent) => {
            if (
                e.key !== 'Tab' ||
                !modalRef.current?.contains(document.activeElement)
            )
                return;
            const focusables = getFocusable();
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first?.focus();
            }
        };

        document.addEventListener('keydown', handleTab);
        getFocusable()[0]?.focus();
        return () => document.removeEventListener('keydown', handleTab);
    });

    const overlay = (
        <motion.div
            className={`modal-overlay ${className || ''}`}
            style={{ zIndex }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.16, ease: 'easeOut' }}
        >
            <motion.div
                ref={modalRef}
                className={`card modal-content ${useFullScreenOnMobile ? 'modal-content-fullscreen' : ''} ${className || ''}`}
                style={{
                    width: '100%',
                    maxWidth,
                    display: 'flex',
                    flexDirection: 'column',
                }}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{
                    duration: reduceMotion ? 0 : 0.16,
                    ease: 'easeOut',
                }}
            >
                <div className="card-header modal-header">
                    <h2 id={titleId} className="card-title">
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close modal"
                        className="modal-close-btn"
                    >
                        ×
                    </button>
                </div>
                <div className="modal-body" style={{ flex: 1, minHeight: 0 }}>
                    {children}
                </div>
                {footer && <div className="modal-actions">{footer}</div>}
            </motion.div>
        </motion.div>
    );

    return createPortal(overlay, document.body);
};

interface SheetVariantProps {
    onClose: () => void;
    title: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    className?: string;
}

const SheetVariant: React.FC<SheetVariantProps> = ({
    onClose,
    title,
    children,
    footer,
    className,
}) => {
    const triggerRef = useRef<HTMLElement | null>(
        document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null,
    );
    const [zIndex] = useState<number>(() => acquireZIndex());

    useMountEffect(() => () => {
        releaseZIndex();
    });

    return (
        <Drawer.Root
            open
            onOpenChange={(o: boolean) => {
                if (!o) onClose();
            }}
        >
            <Drawer.Portal>
                <Drawer.Overlay
                    className={`modal-overlay modal-sheet-overlay ${className || ''}`}
                    style={{ zIndex }}
                />
                <Drawer.Content
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        triggerRef.current?.focus();
                    }}
                    className={`card modal-content modal-sheet ${className || ''}`}
                    style={{ zIndex: zIndex + 1 }}
                    aria-describedby={undefined}
                >
                    <div className="modal-sheet-handle" aria-hidden />
                    <div className="card-header modal-header">
                        <Drawer.Title asChild>
                            <h2 className="card-title">{title}</h2>
                        </Drawer.Title>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close modal"
                            className="modal-close-btn"
                        >
                            ×
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
                    >
                        {children}
                    </div>
                    {footer && <div className="modal-actions">{footer}</div>}
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
};

export default Modal;
